import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import SendMessageIcon from '../../../../app/assets/icons/send-message.svg?react';
import { ChatBubble } from '../ChatBubble/ChatBubble';
import { ChatEmpty } from './ChatEmpty';
import {
	ChatContainer,
	ChatContent,
	ChatMessageBox,
	ChatMessageForm,
	ChatMessageInput,
	ChatMessages,
	ChatMessageSubmit,
} from './style';
import { useTranslationsContext } from '../../../Translations/providers/TranslationsContextProvider';
import { useChatContext } from '../../providers/ChatContextProvider';
import { useMeetContext } from '../../../Meet/providers/MeetContextProvider';
import { ChatMessage } from '../../domain/types';
import { randomUUID } from '../../../../utils/randomUUID';

export function Chat() {
	const chatMessagesRef = useRef<HTMLDivElement | null>(null);
	const submitChatForm = useRef<HTMLButtonElement | null>(null);
	const messageForm = useRef<HTMLFormElement | null>(null);
	const [chatMessage, setChatMessage] = useState('');

	const { participantsNumber } = useMeetContext();
	const { messages, me, sendChatMessageToVideoCall } = useChatContext();
	const { t } = useTranslationsContext();

	const isMyMessage = useCallback((message: ChatMessage) => {
		return message.memberId === me?.memberId;
	}, [me]);

	const lastMessageFromSameMember = useCallback((
		member: string | undefined,
		prevMember: string | undefined
	) => {
		return member === prevMember;
	}, []);

	const getMessage = useCallback((index: number) => {
		if (index < 0) {
			return messages[0];
		}
		return messages[index];
	}, [messages]);

	const submitChatMessage = useCallback((ev: FormEvent<HTMLFormElement>) => {
		ev.preventDefault();
		const message = (ev.target as HTMLFormElement).elements.namedItem('message') as HTMLInputElement;
		if (!message.value) {
			return;
		}
		sendChatMessageToVideoCall(message.value);
		setChatMessage('');
	}, [sendChatMessageToVideoCall]);

	const typingChatMessage = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setChatMessage(event.target.value);
	}, []);

	const submitWithEnter = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && e.shiftKey === false) {
			e.preventDefault();
			submitChatForm?.current?.click();
		}
	}, [submitChatForm]);

	useEffect(() => {
		if (!chatMessagesRef.current) {
			return;
		}

		chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
	}, [messages]);

	return (
		<ChatContainer>
			<ChatContent>
				{messages.length > 0 && (
					<ChatMessages ref={chatMessagesRef}>
						{messages.map((message, index) => (
							<ChatBubble
								key={randomUUID()}
								message={message}
								$mine={isMyMessage(message)}
								sameMember={
									index > 0
										? !lastMessageFromSameMember(message.memberId, getMessage(index - 1).memberId)
										: true
								}
							/>
						))}
					</ChatMessages>
				)}

				{messages.length === 0 && <ChatEmpty />}
			</ChatContent>
			<ChatMessageBox>
				<ChatMessageForm ref={messageForm} onSubmit={submitChatMessage}>
					<input type="submit" hidden />
					<ChatMessageInput
						placeholder={t('chat.type')}
						name="message"
						onChange={typingChatMessage}
						value={chatMessage}
						autoComplete={'off'}
						disabled={participantsNumber < 2}
						onKeyDown={submitWithEnter}
					/>
					<ChatMessageSubmit ref={submitChatForm}>
						<SendMessageIcon />
					</ChatMessageSubmit>
				</ChatMessageForm>
			</ChatMessageBox>
		</ChatContainer>
	);
}
